import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import TppModalGuide from '../../common/TppModalGuide';
import { downloadTppCardExcelTemplate } from '../../../utils/tppCardImportTemplate';
import { tppMutationError } from '../../../utils/tppApiError';
import { TPP_MODAL_CONTENT_CLASS, TppBtnSpinner } from './tppUiShared';

type TppCardImportErrorBody = {
  response?: {
    data?: {
      data?: {
        errors?: Array<{ row: number; message: string; code?: string; firstRow?: number }>;
      };
    };
  };
};

function getTppCardImportRowErrors(
  error: unknown
): Array<{ row: number; message: string; code?: string; firstRow?: number }> | undefined {
  const ax = error as TppCardImportErrorBody;
  return ax?.response?.data?.data?.errors;
}

type TppImportExcelModalProps = {
  closeLabel: string;
  poolKey: string;
  show: boolean;
  onHide: () => void;
  pending: boolean;
  error: unknown;
  onSubmit: (file: File) => void;
  onErrorReset: () => void;
};

export default function TppImportExcelModal({
  closeLabel,
  poolKey,
  show,
  onHide,
  pending,
  error,
  onSubmit,
  onErrorReset,
}: TppImportExcelModalProps) {
  const { t } = useTranslation();
  const excelImportInputRef = useRef<HTMLInputElement>(null);
  const [importFile, setImportFile] = useState<File | null>(null);

  useEffect(() => {
    if (!show) return;
    setImportFile(null);
    onErrorReset();
    if (excelImportInputRef.current) {
      excelImportInputRef.current.value = '';
    }
  }, [show, onErrorReset]);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable contentClassName={TPP_MODAL_CONTENT_CLASS}>
      <Modal.Header closeButton closeLabel={closeLabel} className="border-bottom">
        <Modal.Title>{t('tpp.import_csv_modal_title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light pt-3 pb-2">
        <div className="rounded border overflow-hidden bg-white mb-0">
          <TppModalGuide
            collapsible
            className="mb-0 rounded-0 border-0 border-bottom bg-light"
            introKey="tpp.import_csv_help_intro"
            stepKeys={[
              'tpp.import_csv_help_step1',
              'tpp.import_csv_help_step2',
              'tpp.import_csv_help_step3',
            ]}
            noteKey="tpp.import_csv_help_note"
          />
          <div className="p-3 border-bottom bg-light">
            <p className="small fw-semibold text-body mb-2">
              {t('tpp.import_csv_template_step')}
            </p>
            <p className="small text-muted mb-3">
              {t('tpp.import_csv_template_desc')}
            </p>
            <Button variant="outline-primary" onClick={() => downloadTppCardExcelTemplate(poolKey)}>
              {t('tpp.import_csv_template_excel')}
            </Button>
            <Form.Text muted className="d-block mt-2 mb-1">
              {t('tpp.import_csv_template_filename')}
            </Form.Text>
            <Form.Text muted className="d-block mb-0">
              {t('tpp.import_csv_template_columns')}
            </Form.Text>
          </div>
          <div className="p-3 bg-light">
            <Form.Label className="small fw-semibold text-body mb-2" htmlFor="tpp-import-excel-file">
              {t('tpp.import_csv_upload_step')}
            </Form.Label>
            <Form.Control
              id="tpp-import-excel-file"
              ref={excelImportInputRef}
              type="file"
              className="bg-white"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              disabled={pending}
              aria-label={t('tpp.import_csv_file_aria')}
              onChange={(e) => {
                const f = (e.target as HTMLInputElement).files?.[0] ?? null;
                setImportFile(f);
                onErrorReset();
              }}
            />
            {importFile && (
              <Form.Text muted className="d-block mt-2 mb-0">
                {t('tpp.import_csv_selected_file')} {importFile.name}
              </Form.Text>
            )}
          </div>
        </div>
        {error != null && (
          <Alert variant="danger" className="mt-3 mb-0 py-2 small border-0">
            {(() => {
              const errs = getTppCardImportRowErrors(error);
              if (errs?.length) {
                return (
                  <ul className="mb-0 ps-3">
                    {errs.map((e) => (
                      <li key={`${e.row}-${e.code ?? e.message}`}>
                        {t('tpp.csv_row')} {e.row}:{' '}
                        {e.code
                          ? t(`tpp.import.error.${e.code}`, {
                              defaultValue: e.message,
                              row: e.firstRow ?? e.row,
                            })
                          : e.message}
                      </li>
                    ))}
                  </ul>
                );
              }
              return tppMutationError(error, t);
            })()}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light border-top">
        <Button variant="secondary" onClick={onHide}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          disabled={pending || !importFile}
          onClick={() => importFile && onSubmit(importFile)}
        >
          <TppBtnSpinner show={pending} />
          {t('tpp.import_csv_execute')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
